import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../app/store";
import {
    getWidgetSettingsRequest,
    sendConfirmStripeIntentRequest,
    sendMessageToBotRequest,
    sendStripeIntentRequest
} from "./chatbotApi";
import {generateTemplatedMessage, getBaseUrl} from "./chatbotUtil";
import {VehicleDetails} from "./vehicleDetailsSlice";

export enum Role {
    USER = "user",
    ASSISTANT = "assistant",
    TOOL = "tool"
}

export enum StripePaymentState {
    READY = "READY",
    PROCESSING = "PROCESSING",
    PAID = "PAID",
    DONE = "DONE"
}

export enum MessageType {
    TEXT = "TEXT",
    IMAGE = "IMAGE", // backwards compatibility
    VIDEO = "VIDEO",
    GALLERY = "GALLERY",
    STRIPE = "STRIPE",
    TOOL = "TOOL",
}

export enum BotType {
    AIME = "AIME",
    TMC_AIME = "TMC_AIME",
    AILSA = "AILSA",
    AIDEN = "AIDEN",
}

export const Currency = {
    Pound: 'POUND'
} as const;
export type Currency = typeof Currency[keyof typeof Currency];

export interface WidgetSettings {
    name: string
    botType: BotType,
    showWidget: boolean
    stripePublicKey?: string
    reservationPrice?: number
    reservationCurrency?: Currency
    widgetAppearanceSetting: WidgetAppearanceSetting
}

export interface WidgetAppearanceSetting {
    mainColour: string
    buttonColour: string
    widgetPosition: WidgetPosition
    horizontalOffsetPx: number
    verticalOffsetPx: number
    hidePoweredBy: boolean
    promptPopUpView: boolean
    initialBotMessage: string
    defaultPopUpMessage?: string
    bubbleConvoStarters?: string[]
    chatConvoStarters?: string[]
}

export enum WidgetPosition {
    BOTTOM_LEFT = "BOTTOM_LEFT",
    BOTTOM_RIGHT = "BOTTOM_RIGHT",
}

export enum WidgetPositionEnum {
    LEFT = "LEFT",
    RIGHT = "RIGHT",
    TOP = "TOP",
    BOTTOM = "BOTTOM"
}

export interface ChatMessageResponse {
    role: Role,
    saved: boolean,
    content?: string,
    tool_calls?: any[],
    tool_call_id?: string
}

export interface Message {
    rowMessage?: string,
    messageType: MessageType,
    message?: string,
    vehicle?: VehicleDetails,
    videoLink?: string,
    vehicleList?: VehicleDetails[],
    stripePaymentState?: StripePaymentState,
    stripePaymentId?: number
}

export interface ChatMessage {
    isBot: boolean,
    saved: boolean,
    role: Role,
    message: Message,
    toolCalls?: any[],
    toolCallId?: string,
    time: number
}

export interface FocusVehicle {
    VIN?: string;
    VRM?: string;
}

export interface ChatbotState {
    messages: ChatMessage[];
    open: boolean;
    chat_id?: number;
    focusedVehicle?: FocusVehicle;
    thinking: boolean;
    welcomeBubbleOpen: boolean;
    focusedStripePaymentId?: number;

    stripePublicKey?: string;
    stripeClientSecret?: string;
    stripeErrorMessage?: string;

    reservationPrice?: number;
    reservationCurrency?: Currency;

    isBotInitialized: boolean;
    websiteLocation?: string;
    botId?: string;
    fullUrl: string;
    isMobile: boolean;
    hasWelcomeBubbleTimeoutElapsed: boolean;
    allowSMS: boolean;
    botType?: BotType;
    name?: string;
    userScroll: boolean;
    showWidget: boolean;
    profile?: string;
    platformBaseURL?: string;
    llmEngineBaseURL?: string;
    widgetAppearanceSettings: WidgetAppearanceSetting;
}

const initialState: ChatbotState = {
    showWidget: false,
    messages: [],
    isMobile: false,
    open: false,
    thinking: false,
    welcomeBubbleOpen: true,
    hasWelcomeBubbleTimeoutElapsed: false,
    allowSMS: false,
    isBotInitialized: false,
    userScroll: false,
    fullUrl: '/',
    focusedVehicle: {
        VIN: '',
        VRM: ''
    },
    widgetAppearanceSettings: {
        mainColour: '#0C0126',
        buttonColour: '#0C0126',
        widgetPosition: WidgetPosition.BOTTOM_RIGHT,
        horizontalOffsetPx: 20,
        verticalOffsetPx: 20,
        hidePoweredBy: false,
        promptPopUpView: true,
        initialBotMessage: `Hi, I'm your AI powered sales assistant. I can help you with everything, from helping you find your perfect vehicle, right through to reserving it. What can I do for you today?`,
        defaultPopUpMessage: `Got a question? I am here to help!`,
        bubbleConvoStarters: [],
        chatConvoStarters: []
    }
};

export const sendStripeIntent = createAsyncThunk<string | undefined, number, { state: RootState }>(
    'chatbot/sendStripeIntent',
    async (payload, {getState, dispatch}) => {
        // @ts-ignore
        const state = getState();
        // @ts-ignore
        const botId = state.chatbot.botId;
        const chatId = state.chatbot.chat_id!;
        const messages = state.chatbot.messages;
        // @ts-ignore
        const llmEngineBaseURL = state.chatbot.llmEngineBaseURL
        const focusedStripePaymentIdFromState = state.chatbot.focusedStripePaymentId

        if (!botId) {
            throw new Error("Missed botId")
        }

        if (focusedStripePaymentIdFromState) {
            throw new Error("Please wait on previous payment completion")
        }

        if (!llmEngineBaseURL) {
            throw new Error("Missed llmEngineBaseURL")
        }

        dispatch(updateFocusedStripePaymentId(payload))

        return sendStripeIntentRequest(llmEngineBaseURL, botId, chatId, messages);
    }
);

export const sendConfirmStripeIntent = createAsyncThunk<void, undefined, { state: RootState }>(
    'chatbot/sendConfirmStripeIntent',
    (_payload, {getState}) => {
        const state = getState();
        const stripeClientSecret = state.chatbot.stripeClientSecret
        const chatId = state.chatbot.chat_id
        const botId = state.chatbot.botId
        const llmEngineBaseURL = state.chatbot.llmEngineBaseURL

        if (stripeClientSecret && chatId && botId && llmEngineBaseURL) {
            sendConfirmStripeIntentRequest(llmEngineBaseURL, stripeClientSecret, chatId, botId);
        } else {
            throw new Error("stripeClientSecret must be present")
        }
    }
);

export const sendMessageToBot = createAsyncThunk<ChatMessageResponse[], string, { state: RootState }>(
    'chatbot/sendMessageToBot',
    async (payload, {getState, dispatch}) => {
        // @ts-ignore
        const state = getState();
        dispatch(addHumanMessage(payload));
        // @ts-ignore
        const botId = state.chatbot.botId;
        const chatId = state.chatbot.chat_id;
        const currentUrl = state.chatbot.fullUrl;
        // @ts-ignore
        const llmEngineBaseURL = state.chatbot.llmEngineBaseURL;
        if (!botId) {
            throw new Error("Missed botId")
        }
        if (!llmEngineBaseURL) {
            throw new Error("Missed llmEngineBaseURL")
        }
        const messages = state.chatbot.messages;
        const focusedVehicle = state.chatbot.focusedVehicle;
        return sendMessageToBotRequest(llmEngineBaseURL, botId, chatId, payload, currentUrl, messages, focusedVehicle);
    }
);

export const getWidgetSettings = createAsyncThunk<WidgetSettings, string, { state: RootState }>(
    'chatbot/getWidgetSettings',
    async (payload, {getState}) => {
        if (!payload) {
            throw new Error("Missed botId")
        }
        // @ts-ignore
        const state = getState();
        // @ts-ignore
        const platformBaseURL = state.chatbot.platformBaseURL;
        if (!platformBaseURL) {
            throw new Error("Missed platformBaseURL")
        }
        return getWidgetSettingsRequest(platformBaseURL, payload);
    }
);

export const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        initializeState: (state, {payload, type}) => {
            state.botId = payload.botId
            state.profile = payload.profile ?? "PROD"
            state.platformBaseURL = getBaseUrl("https://platform.motortech.ai", payload.platformBaseURL)
            state.llmEngineBaseURL = getBaseUrl("https://llm.motortech.ai", payload.llmEngineBaseURL)
            state.fullUrl = payload.fullUrl
            state.open = payload.open
            state.isMobile = payload.isMobile
            state.websiteLocation = new URL(payload.websiteLocation).origin

            let chat_id = payload.chatId
            if (!chat_id) {
                chat_id = new Date().getTime();

                if (state.websiteLocation) {
                    window.parent.postMessage(
                        {
                            type: "update-local-storage",
                            key: "chat_id",
                            value: JSON.stringify(chat_id),
                            botId: state.botId
                        },
                        state.websiteLocation
                    )
                }
            }

            state.chat_id = chat_id
            state.welcomeBubbleOpen = payload.welcomeBubbleOpen ?? true
            state.focusedVehicle = payload.focusedVehicle
            chatbotSlice.caseReducers.initMessage(state, {payload: payload.messages, type})
            state.isBotInitialized = true
        },
        addBotMessageManually: (state, {payload, type}) => {
            const messages = state.messages;
            const lastMessage = state.messages[state.messages.length - 1];
            if (messages.length == 1) {
                state.widgetAppearanceSettings.initialBotMessage = payload;
                chatbotSlice.caseReducers.initMessage(state, {payload: [], type})
            } else if (messages.length > 2 && lastMessage?.isBot && lastMessage.message !== payload) {
                chatbotSlice.caseReducers.addBotMessage(state, {
                    payload: [{role: Role.ASSISTANT, content: payload} as ChatMessageResponse], type
                })
            }
        },
        addBotMessage: (state, {payload}: PayloadAction<ChatMessageResponse[]>) => {
            payload.forEach(chatMessage => {
                const content = chatMessage.content
                if (chatMessage.tool_calls || chatMessage.tool_call_id) {
                    state.messages.push({
                        isBot: true,
                        role: chatMessage.role,
                        saved: chatMessage.saved,
                        toolCalls: chatMessage.tool_calls,
                        toolCallId: chatMessage.tool_call_id,
                        message: {
                            rowMessage: content,
                            messageType: MessageType.TOOL,
                        },
                        time: Date.now()
                    })
                } else if (content) {
                    const parsedContent = JSON.parse(content)

                    state.messages.push({
                        isBot: true,
                        role: chatMessage.role,
                        saved: chatMessage.saved,
                        toolCalls: chatMessage.tool_calls,
                        toolCallId: chatMessage.tool_call_id,
                        message: {
                            rowMessage: content,
                            messageType: parsedContent.message_type,
                            message: parsedContent.message,
                            vehicle: parsedContent.vehicle,
                            videoLink: parsedContent.video_link,
                            vehicleList: parsedContent.vehicle_list,
                            stripePaymentState: parsedContent.message_type == MessageType.STRIPE ? StripePaymentState.READY : undefined,
                            stripePaymentId: parsedContent.message_type == MessageType.STRIPE ? Date.now() : undefined,
                        },
                        time: Date.now()
                    })
                }
            })

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "messages",
                        value: JSON.stringify(state.messages),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        addHumanMessage: (state, {payload}: PayloadAction<string>) => {
            state.messages.push({
                isBot: false,
                role: Role.USER,
                saved: false,
                message: {
                    messageType: MessageType.TEXT,
                    rowMessage: payload,
                    message: payload
                },
                time: Date.now()
            });

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "messages",
                        value: JSON.stringify(state.messages),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        openChatbot: (state) => {
            if (!state.open) {
                state.open = true;

                if (state.websiteLocation) {
                    window.parent.postMessage(
                        {
                            type: "update-local-storage",
                            key: "open",
                            value: JSON.stringify(true),
                            botId: state.botId
                        },
                        state.websiteLocation
                    )
                    window.parent.postMessage(
                        {
                            type: "update-local-storage",
                            key: "welcomeBubbleOpen",
                            value: JSON.stringify(false),
                            botId: state.botId
                        },
                        state.websiteLocation
                    )
                }

                state.welcomeBubbleOpen = false

                let chat_id = state.chat_id
                if (!chat_id) {
                    chat_id = new Date().getTime();

                    if (state.websiteLocation) {
                        window.parent.postMessage(
                            {
                                type: "update-local-storage",
                                key: "chat_id",
                                value: JSON.stringify(chat_id),
                                botId: state.botId
                            },
                            state.websiteLocation
                        )
                    }

                    state.chat_id = chat_id;
                }
            }
        },
        closeChatbot: (state) => {
            state.open = false;

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "open",
                        value: JSON.stringify(false),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        setAllMessagesSaved: (state) => {
            state.messages.forEach(message => message.saved = true)

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "messages",
                        value: JSON.stringify(state.messages),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        thinkChatbot: (state, {payload}: PayloadAction<boolean>) => {
            state.thinking = payload;
        },
        initMessage: (state, {payload}: PayloadAction<ChatMessage[]>) => {
            if (!!payload.length) {
                state.messages = payload;
            } else if (!state.messages.length || state.messages.length === 1) {
                state.messages = [generateTemplatedMessage(state.widgetAppearanceSettings.initialBotMessage)];
            }
        },
        focusVehicle: (state, {payload}: PayloadAction<FocusVehicle>) => {
            state.focusedVehicle = payload;
            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "focusedVehicle",
                        value: JSON.stringify(payload),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        markWelcomeBubbleTimeoutAsElapsed: (state) => {
            state.hasWelcomeBubbleTimeoutElapsed = true;
        },
        clearChatHistory: (state) => {
            state.messages = [generateTemplatedMessage(state.widgetAppearanceSettings.initialBotMessage)]
            state.stripeClientSecret = undefined;
            state.stripeErrorMessage = undefined;
            state.focusedVehicle = undefined;
            state.thinking = false;

            const newChatId = new Date().getTime();
            state.chat_id = newChatId;

            if (state.websiteLocation && state.botId) {
                window.parent.postMessage(
                    {
                        type: "remove-from-local-storage",
                        key: "messages",
                        botId: state.botId
                    },
                    state.websiteLocation
                )

                window.parent.postMessage(
                    {
                        type: "remove-from-local-storage",
                        key: "state",
                        botId: state.botId
                    },
                    state.websiteLocation
                )

                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "chat_id",
                        value: JSON.stringify(newChatId),
                        botId: state.botId
                    },
                    state.websiteLocation
                )

                window.parent.postMessage(
                    {
                        type: "check-focused-vehicle"
                    },
                    state.websiteLocation
                )
            }
        },
        closeWelcomeBubble: (state) => {
            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "welcomeBubbleOpen",
                        value: JSON.stringify(false),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
            state.welcomeBubbleOpen = false;
        },
        updateFirstBotMessage: (state, {payload}: PayloadAction<string>) => {
            state.widgetAppearanceSettings.initialBotMessage = payload;
        },
        updateWelcomeBubbleFirstMessage: (state, {payload}: PayloadAction<string>) => {
            state.widgetAppearanceSettings.defaultPopUpMessage = payload;
        },
        updateFullUrl: (state, {payload}: PayloadAction<string>) => {
            state.fullUrl = payload;
        },
        updateFocusedStripePaymentId: (state, {payload}: PayloadAction<number>) => {
            state.focusedStripePaymentId = payload;
        },
        clearFocusedStripePaymentId: (state) => {
            state.focusedStripePaymentId = undefined;
        },
        updateStripePaymentStateInMessage: (state, {payload}: PayloadAction<StripePaymentState>) => {
            state.messages.forEach(message => {
                if (message.message.messageType == MessageType.STRIPE) {
                    if (message.message.stripePaymentId == state.focusedStripePaymentId && message.message.stripePaymentState != StripePaymentState.DONE) {
                        message.message.stripePaymentState = payload;
                    }
                }
            })

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "messages",
                        value: JSON.stringify(state.messages),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getWidgetSettings.fulfilled, (state, {payload, type}) => {
            state.allowSMS = false;
            state.botType = payload.botType;
            state.name = payload.name;
            state.showWidget = payload.showWidget;
            state.stripePublicKey = payload.stripePublicKey;
            state.reservationPrice = payload.reservationPrice;
            state.reservationCurrency = payload.reservationCurrency;
            state.widgetAppearanceSettings = payload.widgetAppearanceSetting;

            const widgetPosition = payload.widgetAppearanceSetting.widgetPosition;
            const horizontalOffsetPx = payload.widgetAppearanceSetting.horizontalOffsetPx;
            const verticalOffsetPx = payload.widgetAppearanceSetting.verticalOffsetPx;

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "change-widget-position",
                        ...(widgetPosition.includes("BOTTOM")
                            ? {bottom: verticalOffsetPx}
                            : {top: verticalOffsetPx}),
                        ...(widgetPosition.includes("RIGHT")
                            ? {right: horizontalOffsetPx}
                            : {left: horizontalOffsetPx}),
                    },
                    state.websiteLocation
                )
            }
        })
        builder.addCase(sendConfirmStripeIntent.fulfilled, (state, {type}) => {
            chatbotSlice.caseReducers.updateStripePaymentStateInMessage(state, {
                payload: StripePaymentState.DONE,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendStripeIntent.fulfilled, (state, {payload, type}) => {
            state.stripeClientSecret = payload;
            chatbotSlice.caseReducers.updateStripePaymentStateInMessage(state, {
                payload: StripePaymentState.PAID,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendStripeIntent.pending, (state, {type}) => {
            chatbotSlice.caseReducers.updateStripePaymentStateInMessage(state, {
                payload: StripePaymentState.PROCESSING,
                type
            })
        })
        builder.addCase(sendStripeIntent.rejected, (state, {error, type}) => {
            state.stripeErrorMessage = error.message;
            chatbotSlice.caseReducers.updateStripePaymentStateInMessage(state, {
                payload: StripePaymentState.READY,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendMessageToBot.fulfilled, (state, action) => {
            state.thinking = false;
            chatbotSlice.caseReducers.addBotMessage(state, action)
            chatbotSlice.caseReducers.setAllMessagesSaved(state)
        })
        builder.addCase(sendMessageToBot.pending, (state, action) => {
            state.thinking = true;
        })
        builder.addCase(sendMessageToBot.rejected, (state, action) => {
            state.thinking = false;
        })
    },
});

export const {
    initializeState,
    addBotMessage,
    addHumanMessage,
    openChatbot,
    closeChatbot,
    thinkChatbot,
    initMessage,
    focusVehicle,
    clearChatHistory,
    updateFirstBotMessage,
    updateFullUrl,
    addBotMessageManually,
    updateFocusedStripePaymentId,
    clearFocusedStripePaymentId,
    updateStripePaymentStateInMessage,
    closeWelcomeBubble,
    markWelcomeBubbleTimeoutAsElapsed
} = chatbotSlice.actions;

export const profileSelector = (state: RootState): string | undefined => state.chatbot.profile;
export const llmEngineBaseURLSelector = (state: RootState): string | undefined => state.chatbot.llmEngineBaseURL;

export const showWidgetSelector = (state: RootState): boolean => state.chatbot.showWidget;
export const isBotOpenSelector = (state: RootState): boolean => state.chatbot.open;
export const allowSMSSelector = (state: RootState): boolean => state.chatbot.allowSMS;
export const botIdSelector = (state: RootState): string | undefined => state.chatbot.botId;
export const botNameSelector = (state: RootState): string | undefined => state.chatbot.name;
export const botTypeSelector = (state: RootState): BotType | undefined => state.chatbot.botType;
export const isMobileSelector = (state: RootState): boolean => state.chatbot.isMobile;
export const isBotThinkingSelector = (state: RootState): boolean => state.chatbot.thinking;
export const messagesSelector = (state: RootState): ChatMessage[] => state.chatbot.messages;
export const welcomeBubbleOpenSelector = (state: RootState): boolean => state.chatbot.welcomeBubbleOpen && state.chatbot.widgetAppearanceSettings.promptPopUpView && state.chatbot.hasWelcomeBubbleTimeoutElapsed;
export const isBotInitializedSelector = (state: RootState): boolean => state.chatbot.isBotInitialized;
export const websiteLocationSelector = (state: RootState): string => state.chatbot.websiteLocation || "/";
export const focusedVehicleSelector = (state: RootState): string | undefined => state.chatbot.focusedVehicle?.VIN;

export const stripePublicKeySelector = (state: RootState): string | undefined => state.chatbot.stripePublicKey;
export const stripeClientSecretSelector = (state: RootState): string | undefined => state.chatbot.stripeClientSecret;
export const stripeErrorMessageSelector = (state: RootState): string | undefined => state.chatbot.stripeErrorMessage;

export const reservationPriceSelector = (state: RootState): number | undefined => state.chatbot.reservationPrice;
export const reservationCurrencySelector = (state: RootState): Currency | undefined => state.chatbot.reservationCurrency;

export const horizontalPositionSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.widgetPosition.includes("RIGHT") ? WidgetPositionEnum.RIGHT : WidgetPositionEnum.LEFT;
export const mainColourSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.mainColour.toUpperCase()
export const buttonColourSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.buttonColour;
export const initialMessageSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.initialBotMessage;
export const defaultPopUpMessageSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.defaultPopUpMessage || "Got a question? I am here to help!";
export const chatConvoStartersSelector = (state: RootState): string[] => state.chatbot.widgetAppearanceSettings.chatConvoStarters || [];
export const bubbleConvoStartersSelector = (state: RootState): string[] => state.chatbot.widgetAppearanceSettings.bubbleConvoStarters || [];
export const verticalOffsetSelector = (state: RootState): number => state.chatbot.widgetAppearanceSettings.verticalOffsetPx;
export const horizontalOffsetSelector = (state: RootState): number => state.chatbot.widgetAppearanceSettings.horizontalOffsetPx;
export const hidePoweredBySelector = (state: RootState): boolean => state.chatbot.widgetAppearanceSettings.hidePoweredBy;
export const widgetPositionSelector = (state: RootState): WidgetPosition => state.chatbot.widgetAppearanceSettings.widgetPosition;

export default chatbotSlice.reducer;
